import { useLayoutEffect, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";
import { Typography } from "@/react/components/shared/uikit/Typography";
import { classNames } from "@/react/helpers/twMergeWithCN";

interface ExpandableBioProps {
  bio: string;
  lineClamp?: number;
}

export const ExpandableBio = ({ bio, lineClamp = 3 }: ExpandableBioProps) => {
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [shouldShowBioSeeMore, setShouldShowBioSeeMore] = useState(false);
  const bioRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!bio || !bioRef.current) return;

    const element = bioRef.current;
    const isOverflowing = element.scrollHeight > element.clientHeight;
    setShouldShowBioSeeMore(isOverflowing);
  }, [bio]);

  const handleBioSeeMore = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsBioExpanded(true);
  };

  return (
    <div className="relative">
      <div
        ref={bioRef}
        // eslint-disable-next-line tailwindcss/no-custom-classname -- I'm dynamically setting the line clamp number
        className={classNames(
          "whitespace-pre-wrap",
          !isBioExpanded && `line-clamp-${lineClamp}`,
        )}
      >
        <Typography.BodySm color="text-default">{bio}</Typography.BodySm>
      </div>
      {shouldShowBioSeeMore && !isBioExpanded && (
        <div className="bg-primary absolute bottom-0 right-0 pl-2">
          <button
            type="button"
            onClick={handleBioSeeMore}
            className="text-dark text-xs font-semibold"
          >
            {t("settings.connect_audience.see_more")}
          </button>
        </div>
      )}
    </div>
  );
};
